import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { ControlDeclaration, FormularKData, FormularKPersonData } from './formular-k.model';

interface FormularKRawValue {
  contractPartner: {
    companyName: string;
    streetAndNumber: string;
    postalCode: string;
    city: string;
    country: string;
  };
  controlDeclaration: ControlDeclaration | null;
  persons: FormularKPersonData[];
  fiduciaryAssets: {
    thirdPartyBeneficialOwner: boolean | null;
  };
}

export function createFormularKForm(formBuilder: FormBuilder): FormGroup {
  return formBuilder.group({
    contractPartner: formBuilder.group({
      companyName: ['', Validators.required],
      streetAndNumber: ['', Validators.required],
      postalCode: ['', Validators.required],
      city: ['', Validators.required],
      country: ['Schweiz', Validators.required]
    }),
    controlDeclaration: [null, Validators.required],
    persons: formBuilder.array([
      createPersonFormGroup(formBuilder)
    ], Validators.required),
    fiduciaryAssets: formBuilder.group({
      thirdPartyBeneficialOwner: [false, Validators.required]
    })
  });
}

export function createPersonFormGroup(formBuilder: FormBuilder): FormGroup {
  return formBuilder.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    mobilePhone: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    streetAndNumber: ['', Validators.required],
    postalCode: ['', Validators.required],
    city: ['', Validators.required],
    country: ['', Validators.required]
  });
}

export function getPersonsFormArray(form: FormGroup): FormArray<FormGroup> {
  return form.get('persons') as FormArray<FormGroup>;
}

export function mapFormularKFormToData(form: FormGroup): FormularKData {
  const rawValue = form.getRawValue() as FormularKRawValue;
  const thirdPartyBeneficialOwner = rawValue.fiduciaryAssets.thirdPartyBeneficialOwner === true;
  const persons = rawValue.persons.map(normalizePerson);
  const filledPersons = persons.filter((person) => !isFormularKPersonEmpty(person));

  return {
    contractPartner: {
      companyName: normalize(rawValue.contractPartner.companyName),
      streetAndNumber: normalize(rawValue.contractPartner.streetAndNumber),
      postalCode: normalize(rawValue.contractPartner.postalCode),
      city: normalize(rawValue.contractPartner.city),
      country: normalize(rawValue.contractPartner.country)
    },
    controlDeclaration: rawValue.controlDeclaration as ControlDeclaration,
    persons: filledPersons.length > 0 ? filledPersons : [normalizePerson(rawValue.persons[0])],
    fiduciaryAssets: {
      thirdPartyBeneficialOwner,
      requiresFormA: thirdPartyBeneficialOwner
    }
  };
}

export function isFormularKPersonEmpty(person: Partial<FormularKPersonData> | null | undefined): boolean {
  if (!person) {
    return true;
  }

  return !hasText(person.firstName)
    && !hasText(person.lastName)
    && !hasText(person.mobilePhone)
    && !hasText(person.email)
    && !hasText(person.streetAndNumber)
    && !hasText(person.postalCode)
    && !hasText(person.city)
    && !hasText(person.country);
}

function normalizePerson(person: Partial<FormularKPersonData> | null | undefined): FormularKPersonData {
  return {
    firstName: normalize(person?.firstName),
    lastName: normalize(person?.lastName),
    mobilePhone: normalize(person?.mobilePhone),
    email: normalize(person?.email),
    streetAndNumber: normalize(person?.streetAndNumber),
    postalCode: normalize(person?.postalCode),
    city: normalize(person?.city),
    country: normalize(person?.country)
  };
}

function hasText(value: string | null | undefined): boolean {
  return normalize(value) !== '';
}

function normalize(value: string | null | undefined): string {
  return (value ?? '').trim();
}
